import { useEffect, useState } from 'react'
import supabase from '../../supabase'
import { PlusCircle, Pencil, Trash } from 'lucide-react'
import AddProductPopup from '../PopUpComponents/AddProductPopup'
import { useToast } from '../Invoice/Toast'
import './UserProfileItems.css'

function UserProfileItems({ modalState, openModal, closeModal }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [formData, setFormData] = useState({
    product_name: '',
    description: '',
    price_selling: '',
  })

  // Delete Popup States
  const [showDeletePopup, setShowDeletePopup] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)

  const { showError, showSuccess, ToastContainer } = useToast()

  // Helper function to create URL-friendly slug
  const createSlug = (text) => {
    if (!text) return ''
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50)
  }

  // Handle URL-based modal state changes
  useEffect(() => {
    if (!modalState || products.length === 0) return

    const { action, id } = modalState

    if (action === 'add') {
      resetForm()
    } else if (action === 'edit' && id) {
      loadProductForEdit(id)
    } else if (!action) {
      resetForm()
    }
  }, [modalState, products])

  // Load product for editing by ID or slug
  const loadProductForEdit = (productIdOrSlug) => {
    // First try to find by product_id
    let product = products.find(p => p.product_id === productIdOrSlug || p.product_id?.toString() === productIdOrSlug)
    
    // If not found, try matching by product_name slug
    if (!product) {
      product = products.find(p => {
        const expectedSlug = createSlug(p.product_name)
        return expectedSlug === productIdOrSlug
      })
    }

    if (product) {
      setEditingProduct(product)
      setFormData({
        product_name: product.product_name,
        description: product.description || '',
        price_selling: product.price_selling?.toString() || '',
      })
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  async function fetchProducts() {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select('product_id, product_name, description, price_selling')
        .eq('is_active', true)
        .order('product_id', { ascending: false })

      if (error) {
        console.error('Error loading products:', error)
        showError('Load Error', 'Failed to load products')
      } else {
        setProducts(data || [])
      }
    } catch (err) {
      console.error('Unexpected error:', err)
      showError('Unexpected Error', 'An unexpected error occurred while loading products')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({ product_name: '', description: '', price_selling: '' })
    setEditingProduct(null)
  }

  const handleAddProductClick = () => {
    resetForm()
    if (openModal) {
      openModal('add')
    }
  }

  const handleAddProductCancel = () => {
    resetForm()
    if (closeModal) closeModal()
  }

  const startEdit = (product, e) => {
    e.stopPropagation()
    if (openModal) {
      openModal('edit', {
        id: product.product_id,
        name: product.product_name
      })
    }
  }

  const handleAddProductSave = async () => {
    if (!formData.product_name.trim()) {
      showError('Validation Error', 'Product name is required')
      return
    }

    if (!formData.price_selling) {
      showError('Validation Error', 'Selling price is required')
      return
    }

    const priceNum = parseFloat(formData.price_selling)
    if (isNaN(priceNum) || priceNum < 0) {
      showError('Validation Error', 'Please enter a valid positive number for selling price')
      return
    }

    setLoading(true)

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()
      
      if (userError || !user) {
        showError('Authentication Error', 'User not authenticated')
        setLoading(false)
        return
      }

      let error

      if (editingProduct) {
        const { error: updateError } = await supabase
          .from('products')
          .update({
            product_name: formData.product_name.trim(),
            description: formData.description.trim(),
            price_selling: priceNum,
          })
          .eq('product_id', editingProduct.product_id)
        error = updateError
      } else {
        const { error: insertError } = await supabase.from('products').insert([
          {
            product_name: formData.product_name.trim(),
            description: formData.description.trim(),
            price_selling: priceNum,
            is_active: true,
            user_id: user.id,
          },
        ])
        error = insertError
      }

      if (error) {
        console.error('Error saving product:', error)
        showError('Save Error', error.message || (editingProduct ? 'Failed to update product' : 'Failed to add product'))
      } else {
        showSuccess('Success', editingProduct ? 'Product updated successfully!' : 'Product added successfully!')
        resetForm()
        if (closeModal) closeModal()
        fetchProducts()
      }
    } catch (err) {
      console.error('Unexpected error:', err)
      showError('Unexpected Error', 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const openDeletePopup = (product, e) => {
    e.stopPropagation()
    setProductToDelete(product)
    setShowDeletePopup(true)
  }

  const closeDeletePopup = () => {
    setShowDeletePopup(false)
    setProductToDelete(null)
  }

  const deleteProduct = async () => {
    if (!productToDelete) return

    const product = productToDelete
    closeDeletePopup()

    setLoading(true)

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()
      
      if (userError || !user) {
        showError('Authentication Error', 'User not authenticated')
        setLoading(false)
        return
      }

      const { error } = await supabase
        .from('products')
        .update({ is_active: false })
        .eq('product_id', product.product_id)

      if (error) {
        console.error('Error deleting product:', error)
        showError('Delete Error', error.message || 'Failed to delete product')
      } else {
        showSuccess('Success', `Product "${product.product_name}" deleted successfully`)
        fetchProducts()
      }
    } catch (err) {
      console.error('Unexpected error:', err)
      showError('Unexpected Error', 'An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) {
      return 'N/A'
    }
    return `₹${Number(price).toFixed(2)}`
  }

  // Determine if popup should show based on modalState
  const showAddProductPopup = modalState?.action === 'add' || modalState?.action === 'edit'

  if (loading && products.length === 0) {
    return (
      <div className="ItemManagement-loading">
        Loading products...
      </div>
    )
  }

  return (
    <>
      <ToastContainer />

      <div className="ItemManagement-container">
        <div className="ItemManagement-header">
          <div className="ItemManagement-header-left">
            <h1 className="ItemManagement-title">Services/Products</h1>
          </div>
          
          <div className="ItemManagement-header-right">
            <button 
              className="ItemManagement-add-btn"
              onClick={handleAddProductClick}
            >
              Add Product
              <PlusCircle size={18} className="add-icon" />
            </button>
          </div>
        </div>

        <div className="ItemManagement-products-section">
          {products.length === 0 ? (
            <div className="ItemManagement-empty">
              <div className="ItemManagement-empty-icon">📦</div>
              <h3 className="ItemManagement-empty-title">No products yet</h3>
              <p className="ItemManagement-empty-description">
                Add your first product above to start managing your inventory.
              </p>
              <button 
                className="ItemManagement-create-first-product-btn"
                onClick={handleAddProductClick}
              >
                Add Your First Product
              </button>
            </div>
          ) : (
            <div className="ItemManagement-content">
              <div className="ItemManagement-table-view">
                <div className="ItemManagement-table-wrapper">
                  <div className="ItemManagement-table-header-container">
                    <table className="ItemManagement-table-header">
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Description</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  
                  <div className="ItemManagement-table-body-container">
                    <table className="ItemManagement-table">
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.product_id} className="ItemManagement-table-row">
                            <td className="ItemManagement-table-cell name-cell">
                              <div className="ItemManagement-table-name">
                                {product.product_name}
                              </div>
                            </td>
                            <td className="ItemManagement-table-cell description-cell">
                              <div className="ItemManagement-table-description">
                                {product.description || '—'}
                              </div>
                            </td>
                            <td className="ItemManagement-table-cell price-cell">
                              <div className="ItemManagement-table-price">
                                {formatPrice(product.price_selling)}
                              </div>
                            </td>
                            <td className="ItemManagement-table-cell actions-cell">
                              <div className="ItemManagement-table-actions">
                                <button
                                  onClick={(e) => startEdit(product, e)}
                                  disabled={loading}
                                  className="ItemManagement-action-btn edit-btn"
                                  title="Edit Product"
                                >
                                  <Pencil size={18} />
                                </button>
                                <button
                                  onClick={(e) => openDeletePopup(product, e)}
                                  disabled={loading}
                                  className="ItemManagement-action-btn prod-delete-btn"
                                  title="Delete Product"
                                >
                                  <Trash size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="ItemManagement-card-view">
                {products.map((product) => (
                  <div key={product.product_id} className="ItemManagement-card">
                    <div className="ItemManagement-card-header">
                      <div className="ItemManagement-card-title">
                        <h4>{product.product_name}</h4>
                        <span className="ItemManagement-card-price">
                          {formatPrice(product.price_selling)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="ItemManagement-card-body">
                      <div className="ItemManagement-card-field">
                        <span className="ItemManagement-card-label">Description:</span>
                        <span className="ItemManagement-card-value">{product.description || 'N/A'}</span>
                      </div>
                    </div>
                    
                    <div className="ItemManagement-card-actions">
                      <button
                        className="ItemManagement-action-btn edit-btn"
                        onClick={(e) => startEdit(product, e)}
                        title="Edit Product"
                        disabled={loading}
                      >
                        <Pencil size={16} />
                        <span className="ItemManagement-btn-text">Edit</span>
                      </button>
                      
                      <button
                        className="ItemManagement-action-btn prod-delete-btn"
                        onClick={(e) => openDeletePopup(product, e)}
                        title="Delete Product"
                        disabled={loading}
                      >
                        <Trash size={16} />
                        <span className="ItemManagement-btn-text">Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showAddProductPopup && (
          <AddProductPopup
            formData={formData}
            setFormData={setFormData}
            onSave={handleAddProductSave}
            onCancel={handleAddProductCancel}
            isEditing={!!editingProduct}
            title={editingProduct ? 'Edit Product' : 'Add New Product'}
            saveButtonText={editingProduct ? 'Update Product' : 'Add Product'}
            loading={loading}
            onRefresh={fetchProducts}
          />
        )}

        {showDeletePopup && productToDelete && (
          <div className="delete-popup-overlay" onClick={closeDeletePopup}>
            <div className="delete-popup" onClick={e => e.stopPropagation()}>
              <div className="delete-popup-header">
                <div className="delete-popup-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 className="delete-popup-title">Delete Product</h3>
              </div>
              
              <div className="delete-popup-content">
                <p className="delete-popup-message">
                  Are you sure you want to delete product <strong>{productToDelete.product_name}</strong>?
                </p>
                
                <div className="delete-popup-info">
                  <div className="delete-popup-info-item">
                    <span className="delete-popup-label">Price:</span>
                    <span className="delete-popup-value">{formatPrice(productToDelete.price_selling)}</span>
                  </div>
                  {productToDelete.description && (
                    <div className="delete-popup-info-item">
                      <span className="delete-popup-label">Description:</span>
                      <span className="delete-popup-value">{productToDelete.description}</span>
                    </div>
                  )}
                </div>

                <div className="delete-popup-warning">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span>This action cannot be undone.</span>
                </div>
              </div>
              
              <div className="delete-popup-actions">
                <button className="delete-popup-btn delete-popup-btn-cancel" onClick={closeDeletePopup}>
                  Cancel
                </button>
                <button className="delete-popup-btn delete-popup-btn-delete" onClick={deleteProduct}>
                  Delete Product
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default UserProfileItems